import { useQueryClient } from '@tanstack/react-query';
import { useCanWriteWorkspaceAsset } from '@vizit/access';
import { useAssetFavorites, useBulkSetAssetFavoriteMutation } from '@vizit/api';
import { useRemoveBenchmarkImageMutation } from '@vizit/api';
import { AlertLevel, useToast } from '@vizit/atoms';
import { CheckSquareFilledIcon, HeartFilledIcon, HeartIcon, Trash01Icon } from '@vizit/icons';
import { type BulkAction, BulkActionBar } from '@vizit/molecules';
import { BenchmarkRemoveImagesDialog } from '@vizit/organisms';
import { settleInBatches } from '@vizit/utils';
import * as React from 'react';
import { refreshBenchmark } from '../../hooks/refresh-benchmark';
import { useBenchmarkCollections } from '../../hooks/use-benchmark-collections';

const REMOVE_BATCH_SIZE = 5;

interface BenchmarkSelectedImage {
  id: string;
  workspaceId: string | null;
  favorite: boolean;
}

interface BenchmarkBulkActionBarProps {
  workspaceId: string;
  benchmarkId: string;
  selectedImages: BenchmarkSelectedImage[];
  /** How many images the benchmark has, for the remove confirm to say what the removal leaves */
  imageCount: number;
  /** Images the benchmark needs to score collections, as the API reports it */
  minimumImages: number;
  /** Collections scored against the benchmark, as the benchmark reports it; the remove confirm names them */
  collectionCount?: number;
  isOnScreenSelected: boolean;
  onSelectVisible: () => void;
  onClear: () => void;
  showFavorite?: boolean;
  onDone?: () => void;
}

function BenchmarkBulkActionBar(props: BenchmarkBulkActionBarProps) {
  const { workspaceId, benchmarkId, selectedImages, isOnScreenSelected, onSelectVisible, onClear, onDone } = props;
  const { imageCount, minimumImages, collectionCount = 0, showFavorite = true } = props;
  const { isFavorite } = useAssetFavorites();
  const canWrite = useCanWriteWorkspaceAsset();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync: removeImage } = useRemoveBenchmarkImageMutation();
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = React.useState(false);
  // The collections the remove confirm names, the delete confirm's read, made only while it is open and in use.
  const { collections: affectedCollections } = useBenchmarkCollections({
    workspaceId,
    benchmarkId: isRemoveDialogOpen && collectionCount > 0 ? benchmarkId : null,
  });
  const { mutate: setFavorites } = useBulkSetAssetFavoriteMutation({
    onError: () => {
      addToast({
        title: 'Could not update favorites',
        description: 'Nothing was changed. Please try again.',
        level: AlertLevel.Error,
      });
    },
  });

  const workspaceOf = (image: BenchmarkSelectedImage) => image.workspaceId ?? workspaceId;
  const isAllFavorite = selectedImages.length > 0 && selectedImages.every((image) => isFavorite(image));
  const canWriteImages = selectedImages.every((image) => canWrite({ workspace_id: workspaceOf(image) }));
  const canWriteBenchmark = canWrite({ workspace_id: workspaceId });
  const selectedWorkspaceIds = new Set(selectedImages.map(workspaceOf));
  const [selectedWorkspaceId] = selectedWorkspaceIds;
  const isSingleWorkspace = selectedWorkspaceIds.size === 1 && Boolean(selectedWorkspaceId);

  const handleFavorite = () => {
    if (!isSingleWorkspace || !selectedWorkspaceId) return;
    setFavorites({
      workspace_id: selectedWorkspaceId,
      image_ids: selectedImages.map((image) => image.id),
      is_favorite: !isAllFavorite,
    });
  };

  const handleRemove = async () => {
    const imageIds = selectedImages.map((image) => image.id);
    const results = await settleInBatches(imageIds, REMOVE_BATCH_SIZE, (image_id) =>
      removeImage({ workspace_id: workspaceId, benchmark_id: benchmarkId, image_id }),
    );
    const failedCount = results.filter((result) => result.status === 'rejected').length;
    setIsRemoveDialogOpen(false);
    if (failedCount < imageIds.length) {
      await refreshBenchmark(queryClient, workspaceId, benchmarkId);
    }
    if (failedCount === 0) {
      onClear();
      onDone?.();
      return;
    }
    addToast({
      title: `${failedCount} of ${imageIds.length} ${imageIds.length === 1 ? 'image was' : 'images were'} not removed`,
      description: `${failedCount === 1 ? 'It stays' : 'They stay'} selected. Please try again.`,
      level: AlertLevel.Error,
    });
  };

  const actions: BulkAction[] = [
    {
      key: 'select-visible',
      label: 'Select visible',
      icon: <CheckSquareFilledIcon size={15} />,
      onPress: onSelectVisible,
      isDisabled: isOnScreenSelected,
      title: 'Select the images on screen',
      disabledReason: 'All images on screen are selected. Scroll to see more',
    },
    ...(showFavorite
      ? [
          {
            key: 'favorite',
            label: isAllFavorite ? 'Unfavorite' : 'Favorite',
            icon: isAllFavorite ? <HeartFilledIcon size={15} /> : <HeartIcon size={15} />,
            onPress: handleFavorite,
            isDisabled: !canWriteImages || !isSingleWorkspace,
            disabledReason: isSingleWorkspace
              ? "You don't have access to change favorites for some of these images"
              : 'Select images from one workspace to change favorites',
          },
        ]
      : []),
    {
      key: 'remove',
      label: 'Remove',
      icon: <Trash01Icon size={15} />,
      onPress: () => setIsRemoveDialogOpen(true),
      tone: 'danger',
      isDisabled: !canWriteBenchmark,
      title: 'Remove selected images from this benchmark',
      disabledReason: "You don't have access to change this benchmark",
    },
  ];

  return (
    <>
      <BulkActionBar count={selectedImages.length} actions={actions} onClear={onClear} />
      <BenchmarkRemoveImagesDialog
        isOpen={isRemoveDialogOpen}
        onOpenChange={setIsRemoveDialogOpen}
        count={selectedImages.length}
        imageCount={imageCount}
        minimumImages={minimumImages}
        affectedCollections={affectedCollections}
        affectedCollectionCount={collectionCount}
        onConfirm={handleRemove}
      />
    </>
  );
}

export { BenchmarkBulkActionBar };
export type { BenchmarkBulkActionBarProps, BenchmarkSelectedImage };
